import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const AdminCareers = () => {
  const [activeTab, setActiveTab] = useState('vacancies'); // 'vacancies' or 'resumes'
  
  // Vacancies State
  const [vacancies, setVacancies] = useState([]);
  const [loadingVacancies, setLoadingVacancies] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    title: '',
    description: '',
    requirements: '',
    is_active: true
  });

  // Resumes State
  const [resumes, setResumes] = useState([]);
  const [loadingResumes, setLoadingResumes] = useState(false);

  useEffect(() => {
    fetchVacancies();
  }, []);

  useEffect(() => {
    if (activeTab === 'resumes') {
      fetchResumes();
    }
  }, [activeTab]);

  // --- Vacancies Logic ---
  const fetchVacancies = async () => {
    setLoadingVacancies(true);
    try {
      const { data, error } = await supabase
        .from('careers')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setVacancies(data || []);
    } catch (error) {
      console.error('Error fetching vacancies:', error);
      alert('Failed to load vacancies: ' + error.message);
    } finally {
      setLoadingVacancies(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      alert("Title and Description are required.");
      return;
    }

    setProcessing(true);
    try {
      if (isEditing && formData.id) {
        // Update existing
        const { error } = await supabase
          .from('careers')
          .update({
            title: formData.title,
            description: formData.description,
            requirements: formData.requirements,
            is_active: formData.is_active
          })
          .eq('id', formData.id);
          
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('careers')
          .insert([{
            title: formData.title,
            description: formData.description,
            requirements: formData.requirements,
            is_active: formData.is_active
          }]);
          
        if (error) throw error;
      }
      
      resetForm();
      fetchVacancies();
    } catch (error) {
      console.error('Error saving vacancy:', error);
      alert('Failed to save vacancy: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (vacancy) => {
    setFormData({
      id: vacancy.id,
      title: vacancy.title,
      description: vacancy.description,
      requirements: vacancy.requirements || '',
      is_active: vacancy.is_active
    });
    setIsEditing(true);
    setActiveTab('vacancies');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteVacancy = async (id) => {
    if (window.confirm("Are you sure you want to delete this vacancy? Associated resumes might also be affected.")) {
      setProcessing(true);
      try {
        // 1. Fetch associated resumes to delete their PDF files from storage
        const { data: resumesToDelete } = await supabase
          .from('resumes')
          .select('resume_url')
          .eq('career_id', id);

        // 2. Delete files from storage
        if (resumesToDelete && resumesToDelete.length > 0) {
          const filePaths = resumesToDelete
            .map(r => r.resume_url?.split('hbb_files/')[1])
            .filter(Boolean);
            
          if (filePaths.length > 0) {
            await supabase.storage.from('hbb_files').remove(filePaths);
          }
        }

        // 3. Delete vacancy (resumes table rows will cascade automatically)
        const { error } = await supabase
          .from('careers')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        fetchVacancies();
      } catch (error) {
        console.error('Error deleting vacancy:', error);
        alert('Failed to delete vacancy: ' + error.message);
      } finally {
        setProcessing(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({ id: null, title: '', description: '', requirements: '', is_active: true });
    setIsEditing(false);
  };

  // --- Resumes Logic ---
  const fetchResumes = async () => {
    setLoadingResumes(true);
    try {
      // Fetch resumes with joined career info
      const { data, error } = await supabase
        .from('resumes')
        .select(`
          *,
          careers (
            title
          )
        `)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setResumes(data || []);
    } catch (error) {
      console.error('Error fetching resumes:', error);
      alert('Failed to load resumes: ' + error.message);
    } finally {
      setLoadingResumes(false);
    }
  };

  return (
    <div className="p-8 min-h-screen bg-[#131313] text-white">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-[#f59e0b]" style={{ fontFamily: 'Bebas Neue' }}>Manage Careers</h2>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 mb-8 overflow-x-auto whitespace-nowrap">
        <button
          onClick={() => setActiveTab('vacancies')}
          className={`px-6 py-3 font-bold uppercase tracking-widest text-sm transition-colors border-b-2 shrink-0 ${activeTab === 'vacancies' ? 'border-[#f59e0b] text-[#f59e0b]' : 'border-transparent text-gray-400 hover:text-white'}`}
        >
          Vacancies
        </button>
        <button
          onClick={() => setActiveTab('resumes')}
          className={`px-6 py-3 font-bold uppercase tracking-widest text-sm transition-colors border-b-2 shrink-0 ${activeTab === 'resumes' ? 'border-[#f59e0b] text-[#f59e0b]' : 'border-transparent text-gray-400 hover:text-white'}`}
        >
          Received Resumes
        </button>
      </div>
      
      {/* --- Vacancies Tab --- */}
      {activeTab === 'vacancies' && (
        <div>
          {/* Form Section */}
          <div className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10 mb-8 shadow-xl">
            <h3 className="text-xl font-semibold mb-4 text-[#d8c3ad]">{isEditing ? 'Edit Vacancy' : 'Post New Vacancy'}</h3>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Job Title</label>
                <input 
                  type="text" 
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g. Senior Sales Executive"
                  className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full focus:outline-none focus:border-[#f59e0b]" 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                <textarea 
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of the role..."
                  rows={3}
                  className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full focus:outline-none focus:border-[#f59e0b]" 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Requirements (Optional)</label>
                <textarea 
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  placeholder="List of requirements, experience needed..."
                  rows={3}
                  className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full focus:outline-none focus:border-[#f59e0b]" 
                />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input 
                  type="checkbox" 
                  id="is_active" 
                  name="is_active" 
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-4 h-4 rounded border-white/10 bg-[#0e0e0e] text-[#f59e0b] focus:ring-[#f59e0b]"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-300">Active (Visible on Website)</label>
              </div>
              
              <div className="flex gap-4 mt-2">
                <button 
                  type="submit" 
                  disabled={processing}
                  className={`flex-1 font-bold py-3 rounded transition-colors flex justify-center items-center gap-2 ${processing ? 'bg-gray-600 text-gray-300 cursor-not-allowed' : 'bg-[#f59e0b] text-[#2a1700] hover:bg-[#ffc174]'}`}
                >
                  {processing ? (
                    <><span className="material-symbols-outlined animate-spin text-sm">sync</span> Processing...</>
                  ) : (
                    isEditing ? 'Update Vacancy' : 'Post Vacancy'
                  )}
                </button>
                {isEditing && (
                  <button 
                    type="button" 
                    onClick={resetForm}
                    disabled={processing}
                    className="px-6 py-3 bg-transparent border border-white/20 text-white rounded hover:bg-white/5 transition-colors font-bold"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* List Section */}
          <div className="bg-[#1c1b1b] rounded-xl border border-white/10 overflow-hidden shadow-xl">
            <div className="p-4 bg-white/5 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#d8c3ad]">Current Vacancies</h3>
              <span className="text-xs bg-[#f59e0b]/20 text-[#f59e0b] px-3 py-1 rounded-full font-bold">{vacancies.length} Total</span>
            </div>
            
            {loadingVacancies ? (
              <div className="p-8 text-center text-gray-400 flex flex-col items-center">
                <span className="material-symbols-outlined animate-spin text-4xl mb-2 text-[#f59e0b]">sync</span>
                <p>Loading Vacancies...</p>
              </div>
            ) : vacancies.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <span className="material-symbols-outlined text-5xl mb-2 opacity-50">work_off</span>
                <p>No vacancies posted yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {vacancies.map(job => (
                  <div key={job.id} className="p-6 hover:bg-white/5 transition-colors flex flex-col md:flex-row gap-6 justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-bold text-white">{job.title}</h4>
                        {job.is_active ? (
                          <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded uppercase tracking-wider font-bold">Active</span>
                        ) : (
                          <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded uppercase tracking-wider font-bold">Closed</span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm whitespace-pre-wrap line-clamp-2">{job.description}</p>
                      <p className="text-xs text-gray-600 mt-4">
                        Posted: {new Date(job.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button 
                        onClick={() => handleEdit(job)} 
                        disabled={processing}
                        className="p-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                        title="Edit Vacancy"
                      >
                        <span className="material-symbols-outlined text-sm">edit</span>
                      </button>
                      <button 
                        onClick={() => handleDeleteVacancy(job.id)} 
                        disabled={processing}
                        className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        title="Delete Vacancy"
                      >
                        <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Resumes Tab --- */}
      {activeTab === 'resumes' && (
        <div className="bg-[#1c1b1b] rounded-xl border border-white/10 overflow-hidden shadow-xl">
          <div className="p-4 bg-white/5 border-b border-white/10 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-[#d8c3ad]">Received Applications</h3>
            <span className="text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full font-bold">{resumes.length} Applications</span>
          </div>
          
          {loadingResumes ? (
            <div className="p-8 text-center text-gray-400 flex flex-col items-center">
              <span className="material-symbols-outlined animate-spin text-4xl mb-2 text-blue-400">sync</span>
              <p>Loading Applications...</p>
            </div>
          ) : resumes.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <span className="material-symbols-outlined text-5xl mb-2 opacity-50">description</span>
              <p>No resumes received yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="border-b border-white/5 bg-[#0e0e0e]">
                    <th className="px-6 py-4 font-normal text-[#a08e7a]">Applicant</th>
                    <th className="px-6 py-4 font-normal text-[#a08e7a]">Applied For</th>
                    <th className="px-6 py-4 font-normal text-[#a08e7a]">Contact</th>
                    <th className="px-6 py-4 font-normal text-[#a08e7a]">Date</th>
                    <th className="px-6 py-4 font-normal text-[#a08e7a] text-right">Resume</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {resumes.map(resume => (
                    <tr key={resume.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 font-medium text-white whitespace-nowrap">{resume.applicant_name}</td>
                      <td className="px-6 py-4 text-gray-300 whitespace-nowrap">
                        {resume.careers?.title || 'Unknown Vacancy'}
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-sm whitespace-nowrap">
                        <a href={`mailto:${resume.email}`} className="hover:text-blue-400 transition-colors">{resume.email}</a>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                        {new Date(resume.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        {resume.resume_url ? (
                          <a 
                            href={resume.resume_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="inline-flex items-center gap-1 bg-[#f59e0b]/10 text-[#f59e0b] hover:bg-[#f59e0b]/20 px-3 py-1.5 rounded transition-colors text-xs font-bold uppercase tracking-wider"
                          >
                            <span className="material-symbols-outlined text-sm">download</span> Open
                          </a>
                        ) : (
                          <span className="text-gray-500 text-xs">No File</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCareers;
